import React, { useCallback } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ErrorOutline,
  Add,
  PhotoCamera,
  InsertDriveFile,
  AutoAwesomeMosaic,
  Close,
  RocketLaunch,
  EmojiEmotions,
  LocationOn,
} from "@mui/icons-material";
import EmojiPicker, { EmojiStyle, Theme } from "emoji-picker-react";
import dummyImage from "../../../assets/dummy_image.jpg";
import { formatText } from "../../../helpers";
import { IAcitvityWhatsAppConversationLogsProps } from "../../../components/Activity/Activity.types";
import { LoadState } from "../../../constants/enums";
import { useDispatchWrapper } from "../../../hooks";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../redux/initialStoreState";
import WhatsappBgImg from "../../../assets/whatsapp_bg_img.jpg";
import {
  clearWhatsAppChat,
  fetchIsWhatsAppConversationStartedAsync,
  fetchScannedPhoneNumberAsync,
  fetchWhatsAppConversationListAsync,
  upsertBaileyMediaMessage,
  upsertMessage,
  upsertWhatsAppMessage,
} from "../../../redux/activity/activityActions";
import {
  IWhatsAppActivity,
  IWhatsAppActivityPayload,
} from "../../../redux/activity/activity.types";
import { CustomTextField } from "../../../components/formsComponents";
import { useFormik } from "formik";
import { useBillingCompanyContext } from "../../../providers/BillingCompanyProvider";
import { useWhatsappMessageReceivedSocket } from "../../../contexts/SocketProvider/SocketProvider.hooks";
import { showMessage } from "../../../redux/messages/messagesActions";
import SmartDisplayIcon from "@mui/icons-material/SmartDisplay";
import SendIcon from "@mui/icons-material/Send";
import { WhatsAppQRScanRequired } from "../../../components/Activity/WhatsAppQRScanRequired";
import { getWhatsappWebServiceValue } from "../../../services/subcriptionApi";
import { useWhatsAppStatus } from "../../../contexts/WhatsAppStatusProvider";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { SelectWhatsappTemplate } from "../../WhatsAppTemplate/components/SelectWhatsappTemplate";
import { useFilePreview } from "../../../hooks/useFilePreview";
import { WhatsAppMessageRow } from "./WhatsAppMessageRow";
import { geocodeByPlaceId } from "react-google-places-autocomplete";
import { MapPicker } from "../components/MapPicker";
import { SelectQuickReplyTemplate } from "../../QuickReplyTemplate/components/SelectQuickReplyTemplate";
import { renderQuickReplyTemplateAsync } from "../../../redux/quickReplyTemplates/quickReplyTemplateAction";
import { SelectEventDialog } from "../../leads/components/SelectEventDialog/SelectEventDialog";
import { uploadFile } from "../../../components/FileUpload/utils";
import {
  getFileExtensionType,
  IFileExtension,
} from "../../../components/FileUploadIcon/helpers";
import { config } from "../../../config";
import { useAuth } from "../../../contexts/AuthProvider";
import produce from "immer";
import { isBaileyProvider, isMetaProvider } from "../../../contexts/AuthProvider/AuthProvider.helpers";

const MESSAGES_PER_PAGE = 10;
export const ActivityWhatsAppLogs = React.forwardRef<
  {
    handleRefresh: () => void;
    populateMessage?: (message: string, quickReplyUuid?: string | null, sourceUuid?: string | null) => void;
  },
  IAcitvityWhatsAppConversationLogsProps
>((props, ref) => {
  const {
    phone,
    useQRSend: propUseQRSend,
    onForwardMessage,
    onRestrictMessage,
    onShowRestrictedMessage,
    visibleRestrictedMessages = new Set(),
    message,
    isFromActivityRoot = false,
    isBlocked = 0,
    leadData,
  } = props;
  const dispatch = useDispatchWrapper();
  const { whatsappService, user } = useAuth();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));
  const mobileEmojiPickerBottom = 72;

  const { previewFile } = useFilePreview();
  const { comapny: companyOutlet } = useBillingCompanyContext();
  const { whatsappNumber } = useWhatsAppStatus();
  const isMetaActive= isMetaProvider(whatsappService);
  const socketResponse = useWhatsappMessageReceivedSocket();

  const { list, loading, error, hasMoreRecords } = useSelector(
    (s: IStoreState) => s.acitivty.whatsApp,
  );

  const chatContainerRef = React.useRef<HTMLDivElement | null>(null);
  const emojiPickerRef = React.useRef<HTMLDivElement | null>(null);

  /** 🔑 SCROLL REFS */
  const isFetchingOldRef = React.useRef(false);
  const isUserAtBottomRef = React.useRef(true);
  const restoreFromBottomRef = React.useRef<number | null>(null);
  const didInitialScrollRef = React.useRef(false);
  const isRestoringScrollRef = React.useRef(false);

  const [messagesList, setMessagesList] = React.useState<IWhatsAppActivity[]>(
    [],
  );
  const [tablePagination, setTablePagination] = React.useState({
    pageNumber: 1,
    rowsInPerPage: MESSAGES_PER_PAGE,
  });

  const [buttonClicked, setButtonClicked] = React.useState<string | null>(null);
  const [saveLoading, setSaveLoading] = React.useState(false);
  const [showAttachmentOptions, setShowAttachmentOptions] =
    React.useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = React.useState(false);
  const [replyingTo, setReplyingTo] = React.useState<IWhatsAppActivity | null>(
    null,
  );
  const [scannedPhoneNumber, setScannedPhoneNumber] = React.useState("");
  const [isStart, setIsStart] = React.useState(1);
  const [showScrollToBottom, setShowScrollToBottom] = React.useState(false);

  const [showSelectWhatsappTemplate, setShowSelectWhatsappTemplate] =
    React.useState<{
      isFromStartConversation: boolean;

      isNormalSelect: boolean;
    }>({
      isFromStartConversation: false,

      isNormalSelect: false,
    });

  const [showSelectQuickReplyTemplate, setShowSelectQuickReplyTemplate] =
    React.useState<boolean>(false);

  const [pendingTemplate, setPendingTemplate] = React.useState<{
    message: string;
    quickReplyUuid: string | null;
  } | null>(null);
  const [selectEventDialogOpen, setSelectEventDialogOpen] = React.useState(false);

  const [showLocationPicker, setShowLocationPicker] =
    React.useState<boolean>(false);

  const currentUseQRSend = propUseQRSend !== undefined ? propUseQRSend : false;

  // AbortController ref to cancel previous requests
  const abortControllerRef = React.useRef<AbortController | null>(null);

  const fetchInitialLogs = useCallback(
    async (disableLoader?: boolean) => {
      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new AbortController for this request
      abortControllerRef.current = new AbortController();

      dispatch(
        fetchWhatsAppConversationListAsync(
          phone,
          1,
          tablePagination.rowsInPerPage,
          true,
          companyOutlet.company_outlet_uuid,
          getCorrectWhatsappWebService(currentUseQRSend),
          undefined,
          disableLoader,
          abortControllerRef.current.signal, // Pass abort signal
        ),
      );
    },
    [
      dispatch,
      phone,
      tablePagination.rowsInPerPage,
      companyOutlet.company_outlet_uuid,
      currentUseQRSend,
    ],
  );

  const fetchMoreLogs = useCallback(
    async (pageNumber: number, onComplete?: () => void) => {
      // Note: We don't cancel fetchMoreLogs requests as they are for pagination
      // and should complete independently
      dispatch(
        fetchWhatsAppConversationListAsync(
          phone,
          pageNumber,
          tablePagination.rowsInPerPage,
          false,
          companyOutlet.company_outlet_uuid,
          getCorrectWhatsappWebService(currentUseQRSend),
          onComplete,
          undefined,
          undefined, // No abort signal for pagination requests
        ),
      );
    },
    [
      dispatch,
      phone,
      tablePagination.rowsInPerPage,
      companyOutlet.company_outlet_uuid,
      currentUseQRSend,
    ],
  );

  /** 🔑 SCROLL HANDLER */
  const handleScroll = React.useCallback(() => {
    const el = chatContainerRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;

    // Are we near bottom?
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 60;

    isUserAtBottomRef.current = isAtBottom;

    setShowScrollToBottom(!isAtBottom);

    // Load older messages
    if (
      scrollTop <= 40 &&
      !isFetchingOldRef.current &&
      loading !== LoadState.InProgress &&
      hasMoreRecords
    ) {
      isFetchingOldRef.current = true;

      // ✅ CAPTURE DISTANCE FROM BOTTOM
      restoreFromBottomRef.current = scrollHeight - scrollTop;

      const nextPage = tablePagination.pageNumber + 1;
      setTablePagination((p) => ({ ...p, pageNumber: nextPage }));

      fetchMoreLogs(nextPage, () => {
        isFetchingOldRef.current = false;
      });
    }
  }, [loading, hasMoreRecords, tablePagination.pageNumber, fetchMoreLogs]);

  React.useLayoutEffect(() => {
    const el = chatContainerRef.current;
    if (!el || messagesList.length === 0) return;

    // 🔁 Restore scroll after older messages
    if (restoreFromBottomRef.current !== null) {
      const distanceFromBottom = restoreFromBottomRef.current;
      restoreFromBottomRef.current = null;

      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight - distanceFromBottom;
      });
      return;
    }

    // ⬇ ALWAYS scroll to bottom when page === 1
    if (tablePagination.pageNumber === 1) {
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    }
  }, [messagesList, tablePagination.pageNumber]);

  const scrollToBottom = React.useCallback((smooth = false) => {
    const el = chatContainerRef.current;
    if (!el) return;

    el.scrollTo({
      top: el.scrollHeight,
      behavior: smooth ? "smooth" : "auto",
    });

    setShowScrollToBottom(false);
  }, []);

  const getCorrectWhatsappWebService = (useQRSend: boolean) => {
    return getWhatsappWebServiceValue(isMetaActive, useQRSend);
  };

  const handleViewDocumnetClick = (fileUrl: string) => {
    previewFile(fileUrl);
  };

  type ReplySnippet =
    | { kind: "text"; text: string }
    | {
      kind: "media";
      mediaType: IFileExtension;
      url: string;
      duration?: number | string;
    }
    | { kind: "none" };

  const getAttachmentUrl = React.useCallback((attachments: any): string | null => {
    if (!attachments) return null;
    if (typeof attachments === "string") return attachments;
    if (Array.isArray(attachments)) return `${config.assets.s3BaseUrl}/${attachments[0]}`
    if (Array.isArray(attachments)) return attachments.find(Boolean) || null;
    if (attachments?.url) return attachments.url;
    if (attachments?.path) return attachments.path;
    return null;
  },
    [],
  );

  const getMessageSnippet = React.useCallback(
    (message?: IWhatsAppActivity | null): ReplySnippet => {
      if (!message) return { kind: "none" };

      if (message.message) return { kind: "text", text: message.message };

      if (Array.isArray(message.content)) {
        const firstText = message.content.find(
          (item: any) => typeof item?.text === "string" && item.text,
        );
        if (firstText?.text) {
          return { kind: "text", text: firstText.text };
        }
      }

      const attachmentUrl = getAttachmentUrl(message.attachments);
      if (attachmentUrl) {
        const mediaType = getFileExtensionType(attachmentUrl);
        const duration =
          (message as any)?.raw_payload?.duration ??
          (message as any)?.raw_payload?.media?.duration ??
          (message as any)?.raw_payload?.media?.seconds ??
          (message as any)?.raw_payload?.seconds;

        return {
          kind: "media",
          mediaType,
          url: attachmentUrl,
          duration,
        };
      }

      if (message.message_type) {
        return { kind: "text", text: formatText(message.message_type) };
      }

      return { kind: "none" };
    },
    [getAttachmentUrl],
  );

  const snippet = getMessageSnippet(replyingTo);
  const formatReplyDuration = (duration?: number | string) => {
    if (duration === undefined || duration === null) return "";
    const totalSeconds = Number(duration);
    if (Number.isNaN(totalSeconds)) return "";
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const renderReplySnippet = () => {
    if (snippet.kind === "media") {
      const formattedDuration = formatReplyDuration(snippet.duration);

      if (snippet.mediaType === "IMAGE") {
        return (
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            <Stack direction="row" spacing={1} alignItems="center" flex={1}>
              <PhotoCamera fontSize="small" color="primary" />
              <Typography variant="body2" sx={{ fontWeight: 400 }}>
                Photo
              </Typography>
            </Stack>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1,
                overflow: "hidden",
                flexShrink: 0,
                backgroundColor: theme.palette.grey[300],
                position: "relative",
                cursor: "pointer",
              }}
              onClick={() => handleViewDocumnetClick(snippet.url)}
            >
              <img
                src={snippet.url}
                alt="Attachment"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = dummyImage;
                }}
              />
            </Box>
          </Stack>
        );
      }

      if (snippet.mediaType === "VIDEO") {
        return (
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            <Stack direction="row" spacing={1} alignItems="center" flex={1}>
              <SmartDisplayIcon fontSize="small" color="primary" />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Video
                </Typography>
                {formattedDuration && (
                  <Typography variant="caption" color="text.secondary">
                    {formattedDuration}
                  </Typography>
                )}
              </Box>
            </Stack>
            <IconButton
              size="small"
              onClick={() => handleViewDocumnetClick(snippet.url)}
              aria-label="Open video"
            >
              <SmartDisplayIcon fontSize="small" color="primary" />
            </IconButton>
          </Stack>
        );
      }

      if (snippet.mediaType === "PDF") {
        return (
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            <Stack direction="row" spacing={1} alignItems="center" flex={1}>
              <InsertDriveFile fontSize="small" color="primary" />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                PDF
              </Typography>
            </Stack>
            <IconButton
              size="small"
              onClick={() => handleViewDocumnetClick(snippet.url)}
              aria-label="Open PDF"
            >
              <InsertDriveFile fontSize="small" color="primary" />
            </IconButton>
          </Stack>
        );
      }

      return (
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            fontSize: "0.85rem",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            wordBreak: "break-word",
            lineHeight: 1.3,
          }}
        >
          Media message
        </Typography>
      );
    }

    if (snippet.kind === "text") {
      return (
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            fontSize: "0.85rem",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            wordBreak: "break-word",
            lineHeight: 1.3,
          }}
        >
          {snippet.text || "Media message"}
        </Typography>
      );
    }

    return null;
  };
  const handleReplySelect = React.useCallback((message: IWhatsAppActivity) => {
    setReplyingTo(message);
    setShowAttachmentOptions(false);
  }, []);

  const clearReplySelection = React.useCallback(() => {
    setReplyingTo(null);
  }, []);

  const { values, handleChange, handleSubmit, resetForm, setFieldValue } =
    useFormik<IWhatsAppActivityPayload & { source_uuid?: string | null }>({
      initialValues: {
        message: "",
        toNumber: "",
        fromNumber: null,
        is_automatic_message: 0,
        objectVariables: { bodyVar: [] },
        image: { link: "" },
        document: { link: "", caption: "" },
        video: { link: "", caption: "" },
        templateName: "",
        company_outlet_uuid: companyOutlet.company_outlet_uuid,
        company_outlet_name: companyOutlet.company_outlet_name,
        attachments: [],
        quick_reply_uuid: null,
        payload: null,
        source_uuid: null,
      },
      validate: (values) => {
        const errors: any = {};
        return errors;
      },

      onSubmit: (values) => {
        if (buttonClicked === "send") {
          sendTextMessage(
            values.message || "",
            values.quick_reply_uuid || null,
            values.payload || null,
            values.source_uuid || null
          );
        }
      },
    });

  const applyRenderedTemplate = React.useCallback((
    message: string,
    quickReplyUuid: string | null,
    sourceUuid: string | null,
  ) => {
    setFieldValue("quick_reply_uuid", quickReplyUuid);
    setFieldValue("source_uuid", sourceUuid);

    if (quickReplyUuid && sourceUuid) {
      dispatch(
        renderQuickReplyTemplateAsync(quickReplyUuid, sourceUuid, (isSuccess, renderedMessage) => {
          setFieldValue("message", isSuccess && renderedMessage ? renderedMessage : message);
        }),
      );
    } else {
      setFieldValue("message", message);
    }
  }, [dispatch, setFieldValue]);

  const handleTemplateSelectionFlow = React.useCallback((message: string, quickReplyUuid: string | null) => {
    if (leadData && leadData.multiple_events > 0) {
      setPendingTemplate({ message, quickReplyUuid });
      setSelectEventDialogOpen(true);
    } else {
      applyRenderedTemplate(message, quickReplyUuid, leadData?.leads_uuid || null);
    }
  }, [leadData, applyRenderedTemplate]);

  const sendTextMessage = React.useCallback((
    messageText: string,
    quickReplyUuid: string | null = null,
    payloadObj: Record<string, string> | null = null,
    sourceUuid: string | null = null,
  ) => {
    setSaveLoading(true);

    // Create optimistic message to show immediately
    const tempConversationId = `temp-${Date.now()}`;
    const optimisticMessage: IWhatsAppActivity = {
      conversation_id: tempConversationId,
      social_media_conversation_id: 0,
      social_media_conversation_unique_id: 0,
      social_media_conversation_uuid: tempConversationId,
      socail_media_name: "WHATSAPP",
      module_name: "WHATSAPP",
      sender_phone: whatsappNumber || "",
      sender_name: "",
      from_number: whatsappNumber || "",
      recipient_number: phone,
      to_number: phone,
      message: messageText,
      message_type: "TEXT",
      reply_from_conversation_id: replyingTo?.conversation_id || null,
      content_type: "TEXT",
      content: [{ type: "BODY", text: messageText || "" }],
      attachments: null,
      raw_payload: null,
      is_automatic_message: 0,
      created_by_uuid: user?.userUuid || null,
      created_by_name: user?.full_name || user?.name || null,
      modified_by_uuid: null,
      modified_by_name: null,
      status: "PENDING",
      create_ts: new Date().toISOString(),
      insert_ts: new Date().toISOString(),
    };

    // Add optimistic message immediately to show in UI
    setMessagesList((prev) => [...prev, optimisticMessage]);
    scrollToBottom(true);

    const payload: IWhatsAppActivityPayload = {
      fromNumber: null,
      toNumber: phone,
      is_automatic_message: 0,
      whatsapp_web_service: currentUseQRSend,
      company_outlet_name: companyOutlet.company_outlet_name,
      company_outlet_uuid: companyOutlet.company_outlet_uuid,
      message: messageText,
    };
    if (quickReplyUuid) {
      payload.payload = payloadObj;
    }
    if (replyingTo?.conversation_id) {
      payload.reply_from_conversation_id = replyingTo.conversation_id;
    }

    // Reset form and clear reply immediately for better UX
    resetForm();
    setReplyingTo(null);
    console.log("socket onSubmit called ");
    dispatch(
      upsertWhatsAppMessage(payload, whatsappService.selectedService, (isSuccess, data) => {
        setSaveLoading(false);
        if (!isSuccess) {
          // Remove optimistic message on failure
          setMessagesList((prev) =>
            prev.filter(
              (msg) => msg.conversation_id !== tempConversationId,
            ),
          );
        }
        if (isBaileyProvider(whatsappService) && isSuccess && data) {
          setMessagesList((prev) => prev.map((msg) => {
            if (msg.conversation_id === tempConversationId) {
              return data;
            }
            return msg;
          }));
        }
      }),
    );
  }, [
    phone,
    whatsappNumber,
    replyingTo,
    currentUseQRSend,
    companyOutlet,
    whatsappService,
    user,
    dispatch,
    resetForm,
  ]);


  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target as Node) &&
        showEmojiPicker
      ) {
        const target = event.target as HTMLElement;
        const isEmojiButton = target.closest("[data-emoji-button]");
        if (!isEmojiButton) {
          setShowEmojiPicker(false);
        }
      }
    };

    if (showEmojiPicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showEmojiPicker]);

  const checkStatus = useCallback(() => {
    if (isBaileyProvider(whatsappService)) {
      setIsStart(1);
    } else {
      dispatch(
        fetchIsWhatsAppConversationStartedAsync(phone, (value) => {
          setIsStart(value);
        }),
      );
    }
  }, [dispatch, phone, whatsappService.selectedService]);

  const checkScannedPhoneNumber = useCallback(() => {
    dispatch(
      fetchScannedPhoneNumberAsync(
        companyOutlet.company_outlet_uuid,
        (value) => {
          setScannedPhoneNumber(value);
        },
      ),
    );
  }, [dispatch, companyOutlet.company_outlet_uuid]);

  const handleRefresh = useCallback(
    (disableLoader?: boolean) => {
      if (!phone) {
        console.error("Phone is undefined or empty!");
        return;
      }

      if (!companyOutlet?.company_outlet_uuid) {
        console.error("company_outlet_uuid is undefined!");
        return;
      }

      checkStatus();
      if (currentUseQRSend) {
        checkScannedPhoneNumber();
      }

      setTablePagination({ pageNumber: 1, rowsInPerPage: MESSAGES_PER_PAGE });
      fetchInitialLogs(disableLoader);
      scrollToBottom(true);
    },
    [
      checkStatus,
      checkScannedPhoneNumber,
      phone,
      companyOutlet.company_outlet_uuid,
      fetchInitialLogs,
    ],
  );

  const handleLocationSelect = async (place: any) => {
    if (!place || !place.value) return;

    const placeId = place.value.place_id;
    if (!placeId) return;

    try {
      setSaveLoading(true);
      // Get the detailed place information using the placeId
      const response = await geocodeByPlaceId(placeId);
      const { geometry, address_components, formatted_address } = response[0];
      const { lat, lng } = geometry.location;

      // Extract address components
      let address = formatted_address || "";
      let name = "";

      // Try to get a meaningful name (establishment name, locality, or route)
      for (const component of address_components) {
        const types = component.types;
        if (types.includes("establishment")) {
          name = component.long_name;
          break;
        } else if (types.includes("locality") && !name) {
          name = component.long_name;
        } else if (types.includes("route") && !name) {
          name = component.long_name;
        }
      }

      // If no name found, use the first part of the address
      if (!name && address) {
        name = address.split(",")[0];
      }

      const payload: IWhatsAppActivityPayload = {
        fromNumber: null,
        toNumber: phone,
        is_automatic_message: 0,
        company_outlet_uuid: companyOutlet.company_outlet_uuid,
        company_outlet_name: companyOutlet.company_outlet_name,
        whatsapp_web_service: currentUseQRSend,
        location: {
          latitude: lat(),
          longitude: lng(),
          name: name || "Selected Location",
          address: address,
        },
      };

      if (replyingTo?.conversation_id) {
        payload.reply_from_conversation_id = replyingTo.conversation_id;
      }

      dispatch(
        upsertWhatsAppMessage(payload, whatsappService.selectedService, (isSuccess) => {
          if (isSuccess) {
            dispatch(
              showMessage({
                type: "success",
                message: "Location sent successfully via WhatsApp",
                displayAs: "snackbar",
              }),
            );
            setReplyingTo(null);
            setShowLocationPicker(false);
          } else {
            dispatch(
              showMessage({
                type: "error",
                message: "Failed to send location via WhatsApp",
                displayAs: "snackbar",
              }),
            );
          }
          setSaveLoading(false);
          setShowLocationPicker(false);
        }),
      );
    } catch (error) {
      console.error("Error fetching location data:", error);
      dispatch(
        showMessage({
          type: "error",
          message: "Failed to get location details",
          displayAs: "snackbar",
        }),
      );
      setSaveLoading(false);
    }
  };

  const handleSendWhatsapp = async (source: string) => {
    if (source === "LOCATION") {
      setShowLocationPicker(true);
      setShowAttachmentOptions(false);
      return;
    }

    const input = document.createElement("input");
    input.type = "file";
    input.accept =
      source === "IMAGE"
        ? "image/*"
        : source === "VIDEO"
          ? "video/*"
          : source === "DOCUMENT"
            ? "application/pdf,.doc,.docx,.txt"
            : source === "AUDIO"
              ? "audio/*"
              : "";

    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      setSaveLoading(true);
      try {
        if (isBaileyProvider(whatsappService)) {
          dispatch(
            upsertBaileyMediaMessage({
              chatsId: phone,
              caption: "",
              mediaType: source.toLowerCase(),
              ptt: "",
              file: file,
            }, (isSuccess, newMessage) => {
              if (isSuccess && newMessage) {
                setMessagesList((prev) => [...prev, newMessage]);
                scrollToBottom(true);
                dispatch(
                  showMessage({
                    type: "success",
                    message: "Message sent successfully via WhatsApp",
                    displayAs: "snackbar",
                  }),
                );
                setReplyingTo(null);
              } else {
                dispatch(
                  showMessage({
                    type: "error",
                    message: "Failed to send message via WhatsApp",
                    displayAs: "snackbar",
                  }),
                );
              }
              setSaveLoading(false); // ✅ Reset here after dispatch callback
            }),
          );
        } else {

          const fileUrl = await uploadFile({
            file: file,
            module_name: "WHATSAPP_MARKET_CAMPAIGN",
            defaultReturn: file.name || "",
            asPayload: {
              company_outlet_uuid: companyOutlet.company_outlet_uuid,
            },
            onWarning: (message) => {
              dispatch(
                showMessage({
                  type: "warning",
                  message: message,
                  displayAs: "snackbar",
                }),
              );
            },
          });

          const payload: IWhatsAppActivityPayload = {
            fromNumber: null,
            toNumber: phone,
            is_automatic_message: 0,
            company_outlet_uuid: companyOutlet.company_outlet_uuid,
            company_outlet_name: companyOutlet.company_outlet_name,
            whatsapp_web_service: currentUseQRSend,
          };

          if (replyingTo?.conversation_id) {
            payload.reply_from_conversation_id = replyingTo.conversation_id;
          }

          if (source === "IMAGE") {
            payload.image = { link: fileUrl };
          } else if (source === "DOCUMENT") {
            payload.document = { link: fileUrl, caption: "" };
          } else if (source === "VIDEO") {
            payload.video = { link: fileUrl, caption: "" };
          }

          dispatch(
            upsertWhatsAppMessage(payload, whatsappService.selectedService, (isSuccess) => {
              if (isSuccess) {
                dispatch(
                  showMessage({
                    type: "success",
                    message: "Message sent successfully via WhatsApp",
                    displayAs: "snackbar",
                  }),
                );
                setReplyingTo(null);
              } else {
                dispatch(
                  showMessage({
                    type: "error",
                    message: "Failed to send message via WhatsApp",
                    displayAs: "snackbar",
                  }),
                );
              }
              setSaveLoading(false); // ✅ Reset here after dispatch callback
            }),
          );
        }
      } catch (error) {
        dispatch(
          showMessage({
            type: "error",
            message: "Failed to upload file",
            displayAs: "snackbar",
          }),
        );
        setSaveLoading(false); // ✅ Also reset on error
      }
    };

    input.click();
  };

  React.useEffect(() => {
    checkStatus();
    if (currentUseQRSend) {
      checkScannedPhoneNumber();
    }
  }, [phone, checkStatus, checkScannedPhoneNumber]);

  React.useEffect(() => {
    if (phone) {
      setMessagesList([]);

      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new AbortController for this request
      abortControllerRef.current = new AbortController();

      dispatch(
        fetchWhatsAppConversationListAsync(
          phone,
          1,
          tablePagination.rowsInPerPage,
          true,
          companyOutlet.company_outlet_uuid,
          getCorrectWhatsappWebService(currentUseQRSend),
          undefined,
          false,
          abortControllerRef.current.signal,
        ),
      );
    }

    return () => {
      // Abort any pending requests when phone changes or component unmounts
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      dispatch(clearWhatsAppChat());
    };
  }, [phone, isMetaActive, dispatch, companyOutlet.company_outlet_uuid, currentUseQRSend, tablePagination.rowsInPerPage]);

  React.useEffect(() => {
    setTablePagination({ pageNumber: 1, rowsInPerPage: MESSAGES_PER_PAGE });
    didInitialScrollRef.current = false;
    restoreFromBottomRef.current = null;
    isRestoringScrollRef.current = false;
    isFetchingOldRef.current = false;
    setReplyingTo(null);
  }, [phone]);

  React.useEffect(() => {
    if (message) {
      setFieldValue("message", message);
    }
  }, [message, setFieldValue]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showAttachmentOptions) {
        const target = event.target as Element;
        if (!target.closest("[data-attachment-options]")) {
          setShowAttachmentOptions(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showAttachmentOptions]);

  React.useImperativeHandle(
    ref,
    () => ({
      handleRefresh,
      populateMessage(message: string, quickReplyUuid?: string | null, sourceUuid?: string | null) {
        setFieldValue("message", message);
        setFieldValue("quick_reply_uuid", quickReplyUuid || null);
        setFieldValue("source_uuid", sourceUuid || null);
      }
    }),
    [handleRefresh, setFieldValue],
  );

  React.useEffect(() => {
    if (!list || list.length === 0) return;

    setMessagesList((prev) => {
      // Preserve optimistic messages (those with temp- prefix) that haven't been replaced yet
      const optimisticMessages = prev.filter((m) =>
        m.conversation_id.startsWith("temp-"),
      );

      if (tablePagination.pageNumber === 1) {
        // Combine redux list with any remaining optimistic messages
        return [...list, ...optimisticMessages];
      }

      const existingIds = new Set(prev.map((m) => m.conversation_id));
      const older = list.filter((m) => !existingIds.has(m.conversation_id));

      return [...older, ...prev]; // prepend
    });
  }, [list, tablePagination.pageNumber]);

  React.useEffect(() => {
    if (!socketResponse) return;

    const currentOutlet = companyOutlet.company_outlet_uuid;
    const messageOutlet = socketResponse.conversation.company_outlet_uuid;
    if (currentOutlet !== messageOutlet) return;

    if (socketResponse.status === "MESSAGE_RECEIVED") {
      const newMessage = socketResponse.conversation;
      console.log("socketResponse phone => ", { phone, scannedPhoneNumber, whatsappNumber })

      const isMessageForCurrentChat = newMessage.recipient_number === phone;
      if (!isMessageForCurrentChat) return;
      setMessagesList((prevMessages) => {
        const messageExists = prevMessages.some(
          (msg) => msg.conversation_id === newMessage.conversation_id,
        );

        if (!messageExists) {
          // Only increment new message count if user is NOT at the bottom
          if (isUserAtBottomRef.current) {
            requestAnimationFrame(() => {
              chatContainerRef.current?.scrollTo({
                top: chatContainerRef.current.scrollHeight,
                behavior: "smooth",
              });
            });
          }

          return [...prevMessages, newMessage as any as IWhatsAppActivity];
        }

        return prevMessages;
      });
      setIsStart(1);
    }

    if (socketResponse.status === "MESSAGE_SENT") {
      const newMessage = socketResponse.conversation;

      const currentCompanyOutletUuid = companyOutlet.company_outlet_uuid;
      const messageToNumber = newMessage.recipient_number;
      const messageCompanyOutletUuid = (newMessage as any).company_outlet_uuid;

      if (messageToNumber !== phone || messageCompanyOutletUuid !== currentCompanyOutletUuid) {
        return;
      }

      // Replace optimistic message (temp-*) with the real message
      setMessagesList((prevMessages) => {
        // Find and remove optimistic messages with temp- prefix for this conversation
        const optimisticIndex = prevMessages.findIndex((msg) =>
          msg.conversation_id.startsWith("temp-"),
        );

        if (optimisticIndex !== -1) {
          // Replace the optimistic message with the real one
          const updatedMessages = [...prevMessages];
          updatedMessages[optimisticIndex] = newMessage as any as IWhatsAppActivity;
          return updatedMessages;
        }

        // If no optimistic message found, check if message already exists
        const messageExists = prevMessages.some(
          (msg) => msg.conversation_id === newMessage.conversation_id,
        );

        if (!messageExists) {
          return [...prevMessages, newMessage as any as IWhatsAppActivity];
        }

        return prevMessages;
      });

      // Also update redux store
      // dispatch(
      //   upsertMessage(socketResponse.conversation as any as IWhatsAppActivity),
      // );
    }

    if (socketResponse.status === "MESSAGE_STATUS_RECEIVED") {
      const newMessage = socketResponse.conversation;

      setMessagesList((prevMessages) => {
        // First, try to find a direct match by conversation_id
        const directMatchIndex = prevMessages.findIndex(
          (msg) => msg.conversation_id === newMessage.conversation_id,
        );

        if (directMatchIndex !== -1) {
          // Direct match found - update status
          const updatedMessages = [...prevMessages];
          updatedMessages[directMatchIndex] = {
            ...updatedMessages[directMatchIndex],
            status: newMessage.status,
          };
          return updatedMessages;
        }

        // No direct match - check if there's an optimistic message (temp-*) that could be this one
        // This handles the case when MESSAGE_SENT doesn't arrive but MESSAGE_STATUS_RECEIVED does
        const optimisticIndex = prevMessages.findIndex(
          (msg) =>
            msg.conversation_id.startsWith("temp-") &&
            (msg.status === "PENDING" ||
              msg.status === "SENT" ||
              msg.status === "INITIATE"),
        );

        if (optimisticIndex !== -1) {
          // Update the optimistic message with the real conversation_id and status
          const updatedMessages = [...prevMessages];
          updatedMessages[optimisticIndex] = {
            ...updatedMessages[optimisticIndex],
            conversation_id: newMessage.conversation_id,
            status: newMessage.status,
          };
          return updatedMessages;
        }

        // No match found at all
        return prevMessages;
      });
    }
  }, [socketResponse, phone, companyOutlet.company_outlet_uuid]);

  React.useEffect(() => {
    if (isFromActivityRoot) {
      handleScroll();
    }
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        width: "100%",
        overflow: "hidden", // Prevent any content from overflowing
        position: "relative", // Create positioning context
      }}
    >
      {/* Render Messages here */}
      <Stack
        ref={chatContainerRef}
        onScroll={handleScroll}
        direction="column"
        spacing={1}
        sx={{
          p: isDesktop ? 1 : 0,
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "clip", // Clip any content that overflows horizontally
          // Remove bottom padding when there are no messages to eliminate gap
          pb: messagesList?.length === 0 ? 0 : 2,
          width: "100%",
          maxWidth: "100%", // Ensure it doesn't exceed container width
          position: "relative", // Ensure proper positioning context
          // Clip absolutely positioned children to stay within bounds
          isolation: "isolate", // Create new stacking context
          borderRadius: messagesList?.length === 0 ? "8px 8px 0 0" : 2, // Curved top edges when empty, all edges when has messages
          ...(messagesList?.length === 0 &&
            loading !== LoadState.InProgress &&
            loading !== LoadState.Failed && {
            minHeight: "400px",
          }),

          backgroundColor: "#efeae2", // WhatsApp beige
          backgroundImage: `url(${WhatsappBgImg})`,
          backgroundRepeat: "repeat", // tile pattern
          backgroundSize: "auto", // no stretch
          backgroundBlendMode: "multiply",
          backgroundAttachment: "local", // Background scrolls with content
          backgroundPosition: "0 0", // Start from top-left
          // Ensure background extends to cover entire area including padding and border
          backgroundOrigin: "border-box",
          boxSizing: "border-box", // Include padding in width calculations
        }}
      >
        {loading === LoadState.InProgress && (
          <>
            <Skeleton height={"30px"} />
            <Skeleton height={"30px"} />
            <Skeleton height={"30px"} />
            {/* <Skeleton height={"30px"} />
              <Skeleton height={"30px"} /> */}
          </>
        )}
        {loading === LoadState.Failed && error && (
          <>
            {error === "WHATSAPP_NOT_SCANNED" ? (
              <WhatsAppQRScanRequired errorType="WHATSAPP_NOT_SCANNED" />
            ) : error === "WHATSAPP_SERVICE_OFF" ? (
              <WhatsAppQRScanRequired errorType="WHATSAPP_SERVICE_OFF" />
            ) : (
              <Box
                minHeight={100}
                display={"flex"}
                flexDirection={"column"}
                justifyContent={"center"}
                alignItems={"center"}
              >
                <Box display={"flex"} justifyContent={"center"} mb={1}>
                  <ErrorOutline />
                </Box>
                <Box display={"flex"} justifyContent={"center"} width={"80%"}>
                  <Typography
                    variant="body1"
                    textAlign={"center"}
                    fontWeight={600}
                  >
                    {error}
                  </Typography>
                </Box>
              </Box>
            )}
          </>
        )}

        {messagesList?.length > 0 && (
          <Box
            sx={{
              width: "100%",
              maxWidth: "100%",
              // overflow: "scroll",
              position: "relative",
            }}
          >
            {messagesList.map((item, index) => {
              return (
                <WhatsAppMessageRow
                  key={index}
                  data={item}
                  currentPhone={phone}
                  whatsappNumber={whatsappNumber}
                  scannedPhoneNumber={isBaileyProvider(whatsappService) ? whatsappService.whatsappNumber : scannedPhoneNumber}
                  propUseQRSend={!!propUseQRSend}
                  messagesList={messagesList}
                  onReplySelect={handleReplySelect}
                  onForwardMessage={onForwardMessage}
                  onRestrictMessage={onRestrictMessage}
                  onShowRestrictedMessage={onShowRestrictedMessage}
                  visibleRestrictedMessages={visibleRestrictedMessages}
                  isFromActivityRoot={isFromActivityRoot}
                />
              );
            })}
          </Box>
        )}

        {messagesList?.length === 0 &&
          loading !== LoadState.InProgress &&
          loading !== LoadState.Failed && (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                minHeight: "400px",
              }}
            ></Box>
          )}
      </Stack>

      {/* bottom Message form section */}
      <Stack
        sx={{
          position: "relative",
          // Use WhatsApp background when there are no messages, otherwise use paper background
          backgroundColor:
            messagesList?.length === 0
              ? "#efeae2"
              : theme.palette.background.paper,
          backgroundImage:
            messagesList?.length === 0 ? `url(${WhatsappBgImg})` : "none",
          backgroundRepeat: messagesList?.length === 0 ? "repeat" : "no-repeat",
          backgroundSize: messagesList?.length === 0 ? "auto" : "auto",
          backgroundBlendMode:
            messagesList?.length === 0 ? "multiply" : "normal",
          backgroundAttachment: messagesList?.length === 0 ? "local" : "scroll",
          // Align background pattern to continue seamlessly from chat area
          backgroundPosition: messagesList?.length === 0 ? "0 0" : "0 0",
          backgroundOrigin:
            messagesList?.length === 0 ? "border-box" : "padding-box",
          // Remove border and top padding when showing WhatsApp background for seamless appearance
          borderTop:
            messagesList?.length === 0
              ? "none"
              : `1px solid ${theme.palette.divider}`,
          pt: messagesList?.length === 0 ? 0 : 1, // Remove top padding when empty to eliminate gap
          px: 1,
          pb: 1,
          borderRadius: messagesList?.length === 0 ? "0 0 8px 8px" : 0, // Curved bottom edges when showing WhatsApp background
          flexShrink: 0,
        }}
      >
        {loading === LoadState.InProgress &&
          tablePagination.pageNumber === 1 ? (
          <>
            <Skeleton height={"40px"} />
          </>
        ) : (
          <>
            {showAttachmentOptions && (
              <Box
                data-attachment-options
                sx={{
                  position: "absolute",
                  bottom: "100%",
                  left: isDesktop ? 16 : 8,
                  mb: 1,
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: 2,
                  boxShadow: theme.shadows[8],
                  border: `1px solid ${theme.palette.divider}`,
                  p: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  minWidth: 200,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 1,
                    borderRadius: 1,
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                  onClick={() =>
                    setShowSelectWhatsappTemplate({
                      isFromStartConversation: false,

                      isNormalSelect: true,
                    })
                  }
                >
                  <AutoAwesomeMosaic
                    sx={{ color: theme.palette.primary.main }}
                  />
                  <Typography variant="body2">Select Template</Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 1,
                    borderRadius: 1,
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                  onClick={() => {
                    if (props.onQuickReplyClick) {
                      props.onQuickReplyClick();
                    } else {
                      setShowSelectQuickReplyTemplate(true);
                    }
                  }}
                >
                  <RocketLaunch sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="body2">Quick Reply</Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 1,
                    borderRadius: 1,
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                  onClick={() => {
                    handleSendWhatsapp("IMAGE");
                    setShowAttachmentOptions(false);
                  }}
                >
                  <PhotoCamera sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="body2">Photo / Image</Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 1,
                    borderRadius: 1,
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                  onClick={() => {
                    handleSendWhatsapp("VIDEO");
                    setShowAttachmentOptions(false);
                  }}
                >
                  <SmartDisplayIcon
                    sx={{ color: theme.palette.primary.main }}
                  />
                  <Typography variant="body2">Video</Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 1,
                    borderRadius: 1,
                    // cursor: documentSendingDisabled ? "not-allowed" : "pointer",
                    cursor: "pointer",

                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                  onClick={() => {
                    handleSendWhatsapp("DOCUMENT");
                    setShowAttachmentOptions(false);
                  }}
                >
                  <InsertDriveFile
                    sx={{
                      color: theme.palette.primary.main,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: "inherit",
                    }}
                  >
                    Document
                  </Typography>
                </Box>

                {/* <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 1,
                    borderRadius: 1,
                    cursor: "pointer",

                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                  onClick={() => {
                    handleSendWhatsapp("LOCATION");
                    setShowAttachmentOptions(false);
                  }}
                >
                  <LocationOn
                    sx={{
                      color: theme.palette.primary.main,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: "inherit",
                    }}
                  >
                    Location
                  </Typography>
                </Box> */}
              </Box>
            )}

            <form onSubmit={handleSubmit}>
              {replyingTo && (
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    borderLeft: "4px solid #008069",
                    backgroundColor: "#F0F2F5",
                    p: 1,
                    mb: 1,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Box flexGrow={1}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: "#008069",
                          marginBottom: 0.5,
                          display: "block",
                        }}
                      >
                        Replying to{" "}
                        {replyingTo.created_by_name
                          ? replyingTo.created_by_name
                          : "You"}
                      </Typography>

                      {renderReplySnippet()}
                    </Box>
                    <IconButton
                      size="small"
                      onClick={clearReplySelection}
                      aria-label="Cancel reply"
                      sx={{
                        color: theme.palette.grey[500],
                        padding: 0.5,
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Stack>
                </Paper>
              )}

              {isStart || currentUseQRSend ? (
                <Grid container spacing={isDesktop ? 2 : 1} alignItems="center">
                  {!(currentUseQRSend) && (
                    <Grid item xs="auto">
                      <Button
                        disabled={
                          (currentUseQRSend &&
                            (error === "WHATSAPP_NOT_SCANNED" ||
                              error === "WHATSAPP_SERVICE_OFF")) ||
                          isBlocked === 1
                        }
                        data-attachment-options
                        sx={{
                          position: "relative",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 40,
                          height: 40,
                          minWidth: 40,
                          borderRadius: "50%",
                          backgroundColor: theme.palette.grey[200],
                          padding: 0,
                          "&:hover": {
                            backgroundColor: theme.palette.grey[300],
                          },
                          "&:disabled": {
                            backgroundColor: theme.palette.grey[100],
                            color: theme.palette.grey[400],
                          },
                        }}
                        onClick={() =>
                          setShowAttachmentOptions(!showAttachmentOptions)
                        }
                      >
                        <Add sx={{ fontSize: 20 }} />
                      </Button>
                    </Grid>
                  )}

                  <Grid item xs="auto" position="relative">
                    <Button
                      data-emoji-button
                      disabled={
                        (currentUseQRSend &&
                          (error === "WHATSAPP_NOT_SCANNED" ||
                            error === "WHATSAPP_SERVICE_OFF")) ||
                        isBlocked === 1
                      }
                      sx={{
                        position: "relative",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 40,
                        height: 40,
                        minWidth: 40,
                        borderRadius: "50%",
                        backgroundColor: theme.palette.grey[200],
                        padding: 0,
                        "&:hover": {
                          backgroundColor: theme.palette.grey[300],
                        },
                        "&:disabled": {
                          backgroundColor: theme.palette.grey[100],
                          color: theme.palette.grey[400],
                        },
                      }}
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    >
                      <EmojiEmotions sx={{ fontSize: 20 }} />
                    </Button>
                    {showEmojiPicker && (
                      <Box
                        ref={emojiPickerRef}
                        sx={{
                          position: isDesktop ? "absolute" : "fixed",
                          bottom: isDesktop
                            ? "100%"
                            : `${mobileEmojiPickerBottom}px`,
                          left: isDesktop ? 0 : 12,
                          right: isDesktop ? "auto" : 12,
                          mb: isDesktop ? 1 : 0,
                          zIndex: 50,
                          boxShadow: 3,
                          borderRadius: 2,
                          overflow: "hidden",
                          width: isDesktop ? 350 : "auto",
                          maxWidth: isDesktop ? "none" : "calc(100vw - 24px)",
                        }}
                      >
                        <EmojiPicker
                          onEmojiClick={(ed: any) => {
                            setFieldValue(
                              "message",
                              (values.message || "") + ed.emoji,
                            );
                          }}
                          width="100%"
                          height={isDesktop ? 400 : 320}
                          emojiStyle={EmojiStyle.APPLE}
                          theme={Theme.AUTO}
                          previewConfig={{ showPreview: false }}
                        />
                      </Box>
                    )}
                  </Grid>

                  <Grid item xs>
                    <CustomTextField
                      fullWidth
                      id="message"
                      value={values.message}
                      multiline
                      minRows={1}
                      maxRows={3}
                      disabled={
                        (currentUseQRSend &&
                          (error === "WHATSAPP_NOT_SCANNED" ||
                            error === "WHATSAPP_SERVICE_OFF")) ||
                        isBlocked === 1
                      }
                      sx={{
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: 2,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          fontSize: isDesktop ? "1rem" : "0.875rem",
                          alignItems: "flex-end",
                          "& textarea": {
                            overflow: "auto !important",
                          },
                        },
                      }}
                      onChange={(e) => {
                        handleChange(e);
                        if (values.quick_reply_uuid) {
                          setFieldValue("quick_reply_uuid", null);
                          setFieldValue("payload", null);
                          setFieldValue("source_uuid", null);
                        }
                      }}
                      onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          if ((values?.message?.trim() || "").length > 0) {
                            setButtonClicked("send");
                            handleSubmit();
                          }
                        }
                      }}
                      placeholder={isBlocked === 1 ? "This contact is blocked" : "Type a message"}
                      size={isDesktop ? "medium" : "small"}
                    />
                  </Grid>

                  <Grid item xs="auto">
                    <IconButton
                      type="submit"
                      onClick={() => setButtonClicked("send")}
                      disabled={(values.message?.trim() || "").length < 1 || isBlocked === 1}
                      sx={{
                        bgcolor: "#00d757",
                        // backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        borderRadius: 2,
                        width: isDesktop ? 40 : 36,
                        height: isDesktop ? 40 : 36,
                        minWidth: isDesktop ? 40 : 36,
                        "&:hover": {
                          backgroundColor: theme.palette.primary.dark,
                        },
                        "&:disabled": {
                          backgroundColor: theme.palette.grey[400],
                          color: theme.palette.grey[600],
                        },
                      }}
                    >
                      <SendIcon fontSize={isDesktop ? "medium" : "small"} />
                    </IconButton>
                  </Grid>

                  {showScrollToBottom && (
                    <Grid
                      item
                      xs="auto"
                      position={"absolute"}
                      right={4}
                      bottom={70}
                    >
                      <Tooltip title="Scroll to bottom">
                        <Button
                          data-attachment-options
                          sx={{
                            position: "relative",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 40,
                            height: 40,
                            minWidth: 40,
                            borderRadius: "50%",
                            zIndex: 10,
                            backgroundColor: theme.palette.grey[200],
                            paddingBottom: 2,
                            "&:hover": {
                              backgroundColor: theme.palette.grey[300],
                            },
                            "&:disabled": {
                              backgroundColor: theme.palette.grey[100],
                              color: theme.palette.grey[400],
                            },
                          }}
                          onClick={() => {
                            scrollToBottom(true);
                          }}
                        >
                          <ArrowBackIosIcon
                            sx={{ fontSize: 20, rotate: "-90deg" }}
                          />
                        </Button>
                      </Tooltip>
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Grid container spacing={isDesktop ? 2 : 1} alignItems="center">
                  {isMetaActive && (
                    <>
                      <Grid item xs>
                        <Button
                          variant="contained"
                          fullWidth
                          size={"small"}
                          type="submit"
                          // onClick={() => setButtonClicked("start")}
                          onClick={() =>
                            setShowSelectWhatsappTemplate({
                              isFromStartConversation: true,

                              isNormalSelect: false,
                            })
                          }
                          disabled={saveLoading || isBlocked === 1}
                          startIcon={
                            saveLoading ? (
                              <CircularProgress color="inherit" size={16} />
                            ) : null
                          }
                          endIcon={
                            <SendIcon
                              sx={{
                                color: `${"white"}`,
                              }}
                            />
                          }
                          sx={{
                            borderRadius: 2,
                            height: isDesktop ? 40 : 36,
                          }}
                        >
                          {saveLoading
                            ? "Sending..."
                            : "Select template to Start Conversation"}
                        </Button>
                      </Grid>
                    </>
                  )}
                  {!isMetaActive && (
                    <>
                      {" "}
                      <Grid item xs="auto" position="relative">
                        <Button
                          data-emoji-button
                          disabled={
                            (currentUseQRSend &&
                              (error === "WHATSAPP_NOT_SCANNED" ||
                                error === "WHATSAPP_SERVICE_OFF")) ||
                            isBlocked === 1
                          }
                          sx={{
                            position: "relative",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 40,
                            height: 40,
                            minWidth: 40,
                            borderRadius: "50%",
                            backgroundColor: theme.palette.grey[200],
                            padding: 0,
                            "&:hover": {
                              backgroundColor: theme.palette.grey[300],
                            },
                            "&:disabled": {
                              backgroundColor: theme.palette.grey[100],
                              color: theme.palette.grey[400],
                            },
                          }}
                          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                        >
                          <Add sx={{ fontSize: 20 }} />
                        </Button>
                        {showEmojiPicker && (
                          <Box
                            ref={emojiPickerRef}
                            sx={{
                              position: isDesktop ? "absolute" : "fixed",
                              bottom: isDesktop
                                ? "100%"
                                : `${mobileEmojiPickerBottom}px`,
                              left: isDesktop ? 0 : 12,
                              right: isDesktop ? "auto" : 12,
                              mb: isDesktop ? 1 : 0,
                              zIndex: 50,
                              boxShadow: 3,
                              borderRadius: 2,
                              overflow: "hidden",
                              width: isDesktop ? 350 : "auto",
                              maxWidth: isDesktop
                                ? "none"
                                : "calc(100vw - 24px)",
                            }}
                          >
                            <EmojiPicker
                              onEmojiClick={(ed: any) => {
                                setFieldValue(
                                  "message",
                                  (values.message || "") + ed.emoji,
                                );
                              }}
                              width="100%"
                              height={isDesktop ? 400 : 320}
                              emojiStyle={EmojiStyle.APPLE}
                              theme={Theme.AUTO}
                              previewConfig={{ showPreview: false }}
                            />
                          </Box>
                        )}
                      </Grid>
                      <Grid item xs>
                        <CustomTextField
                          fullWidth
                          id="message"
                          value={values.message}
                          multiline
                          rows={1}
                          disabled={
                            (currentUseQRSend &&
                              (error === "WHATSAPP_NOT_SCANNED" ||
                                error === "WHATSAPP_SERVICE_OFF")) ||
                            isBlocked === 1
                          }
                          sx={{
                            backgroundColor: theme.palette.background.paper,
                            borderRadius: 2,
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              fontSize: isDesktop ? "1rem" : "0.875rem",
                              alignItems: "flex-end",
                            },
                          }}
                          onChange={(e) => {
                            handleChange(e);
                            if (values.quick_reply_uuid) {
                              setFieldValue("quick_reply_uuid", null);
                              setFieldValue("payload", null);
                              setFieldValue("source_uuid", null);
                            }
                          }}
                          onKeyDown={(
                            e: React.KeyboardEvent<HTMLDivElement>,
                          ) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              if ((values.message || "").length > 0) {
                                setButtonClicked("send");
                                handleSubmit();
                              }
                            }
                          }}
                          placeholder={isBlocked === 1 ? "This contact is blocked" : "Type message...."}
                          size={isDesktop ? "medium" : "small"}
                        />
                      </Grid>
                      <Grid item xs="auto">
                        <Button
                          variant="contained"
                          size={isDesktop ? "medium" : "small"}
                          type="submit"
                          onClick={() => setButtonClicked("send")}
                          disabled={
                            (values.message || "").length < 1 || isBlocked === 1
                          }
                          // startIcon={
                          //   saveLoading ? (
                          //     <CircularProgress color="inherit" size={16} />
                          //   ) : null
                          // }
                          sx={{
                            borderRadius: 2,
                            minWidth: isDesktop ? 80 : 60,
                            height: isDesktop ? 40 : 36,
                          }}
                        >
                          {/* {saveLoading ? "Sending..." : "Send"} */}
                          send
                        </Button>
                      </Grid>
                    </>
                  )}
                </Grid>
              )}
            </form>
          </>
        )}
      </Stack>

      {(showSelectWhatsappTemplate.isFromStartConversation ||
        showSelectWhatsappTemplate.isNormalSelect) && (
          <SelectWhatsappTemplate
            open={true}
            toNumber={phone}
            onClose={() => {
              setShowSelectWhatsappTemplate({
                isFromStartConversation: false,

                isNormalSelect: false,
              });
              setSaveLoading(false);
            }}
            isFromStartConversation={
              showSelectWhatsappTemplate.isFromStartConversation
            }
          />
        )}

      {showSelectQuickReplyTemplate && (
        <SelectQuickReplyTemplate
          open={true}
          toNumber={phone}
          leadData={leadData}
          onClose={() => {
            setShowSelectQuickReplyTemplate(false);
            setSaveLoading(false);
          }}
          onSelectTemplate={(message, quickReplyUuid) => {
            handleTemplateSelectionFlow(message, quickReplyUuid || null);
            setShowSelectQuickReplyTemplate(false);
          }}
        />
      )}

      {selectEventDialogOpen && leadData && leadData.multiple_events_uuid && (
        <SelectEventDialog
          open={true}
          multipleEventsUuid={leadData.multiple_events_uuid}
          onClose={() => {
            setSelectEventDialogOpen(false);
            setPendingTemplate(null);
          }}
          onSelect={(leadsUuid) => {
            setSelectEventDialogOpen(false);
            if (pendingTemplate) {
              applyRenderedTemplate(pendingTemplate.message, pendingTemplate.quickReplyUuid, leadsUuid);
            }
            setPendingTemplate(null);
          }}
        />
      )}

      {showLocationPicker && (
        <MapPicker
          open={showLocationPicker}
          onClose={() => setShowLocationPicker(false)}
          onHandleLocationSelect={handleLocationSelect}
          isFromChatModule
        />
      )}
    </Box>
  );
});
